import { CloseOutlined } from '@ant-design/icons';
import {
  ComponentPropsWithRef,
  createContext,
  CSSProperties,
  forwardRef,
  ReactNode,
  useContext,
} from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import { __DEV__ } from '../../utils';
import { IconButton } from '../icon-button';

interface DialogContextProps {
  isOpen: boolean;
  isFull?: boolean;
  onClose?: () => void;
}

interface DialogProps extends DialogContextProps {
  children: ReactNode;
}

const DialogContext = createContext<DialogContextProps | undefined>(undefined);

const useDialogContext = () => {
  const context = useContext(DialogContext);

  if (context === undefined) {
    throw new Error('useDialogContext must be used under DialogContext');
  }

  return context;
};

const Dialog = (props: DialogProps) => {
  const { children, isOpen = false, isFull = false, onClose } = props;

  return (
    <DialogContext.Provider value={{ isOpen, isFull, onClose }}>
      {isOpen && children}
    </DialogContext.Provider>
  );
};

export type DialogCloseButtonProps = ComponentPropsWithRef<'button'>;

const DialogCloseButton = forwardRef<HTMLButtonElement, DialogCloseButtonProps>(
  (_props, ref) => {
    const { onClose } = useDialogContext();

    const handleClose = () => {
      onClose?.();
    };

    const styles: CSSProperties = {
      position: 'absolute',
      right: 10,
      top: 10,
    };

    return (
      <IconButton
        ref={ref}
        icon={CloseOutlined}
        mass="small"
        variant="tertiary"
        onClick={handleClose}
        style={styles}
      />
    );
  }
);

if (__DEV__) {
  DialogCloseButton.displayName = 'DialogCloseButton';
}

export type DialogContentProps = ComponentPropsWithRef<'div'> & {
  align?: 'start' | 'end' | 'center';
};

const DialogContent = forwardRef<HTMLDivElement, DialogContentProps>(
  (props, ref) => {
    const { children, align = 'center', ...rest } = props;
    const { isFull } = useDialogContext();

    const dialogContainerStyles: CSSProperties = {
      display: 'flex',
      alignItems: align,
      justifyContent: 'center',
      width: '100vw',
      height: '100vh',
      position: 'fixed',
      left: 0,
      top: 0,
      zIndex: 1400,
    };

    const dialogStyles: CSSProperties = {
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      width: isFull ? '100%' : '28rem',
      height: isFull ? '100%' : 'auto',
      outline: 0,
      background: '#ffffff',
      maxWidth: '100%',
      maxHeight: '100%',
      borderRadius: '16px',
      ...rest.style,
    };

    return (
      <RemoveScroll>
        <div
          ref={ref}
          tabIndex={-1}
          style={dialogContainerStyles}
          className={RemoveScroll.classNames.fullWidth}
        >
          <div style={dialogStyles}>{children}</div>
        </div>
      </RemoveScroll>
    );
  }
);

if (__DEV__) {
  DialogContent.displayName = 'DialogContent';
}

export type DialogBodyProps = ComponentPropsWithRef<'div'>;

const DialogBody = forwardRef<HTMLDivElement, DialogBodyProps>((props, ref) => {
  const { children, ...rest } = props;

  // IMPROVEMENTS: Spacing from vars?
  const styles: CSSProperties = {
    flex: 1,
    overflow: 'auto',
    padding: '1rem',
  };

  return (
    <div ref={ref} style={styles} {...rest}>
      {children}
    </div>
  );
});

if (__DEV__) {
  DialogBody.displayName = 'DialogBody';
}

export type DialogHeaderProps = ComponentPropsWithRef<'div'>;

const DialogHeader = forwardRef<HTMLDivElement, DialogBodyProps>(
  (props, ref) => {
    const { children, ...rest } = props;

    // IMPROVEMENTS: Spacing from vars?
    // TODO: Make styles overridable
    const styles: CSSProperties = {
      flex: 0, // Only the body can grow

      // Begin: Random Classes for example
      fontWeight: 700,
      fontSize: 20,
      borderBottom: '1px solid #cccccc',
      // End: Random Classes for example

      padding: '1rem',
    };

    return (
      <div ref={ref} style={styles} {...rest}>
        {children}
      </div>
    );
  }
);

if (__DEV__) {
  DialogHeader.displayName = 'DialogHeader';
}

export type DialogOverlayProps = ComponentPropsWithRef<'div'>;

const DialogOverlay = forwardRef<HTMLDivElement, DialogOverlayProps>(
  (props, ref) => {
    const { children, ...rest } = props;

    const styles: CSSProperties = {
      position: 'fixed',
      left: 0,
      top: 0,
      width: '100vw',
      height: '100vh',
      background: '#0000007a',
      zIndex: 1400,
    };

    return <div ref={ref} style={styles} {...rest} />;
  }
);

if (__DEV__) {
  DialogOverlay.displayName = 'DialogOverlay';
}

export {
  DialogOverlay,
  DialogContent,
  DialogBody,
  DialogHeader,
  Dialog,
  DialogCloseButton,
};
